import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { labels } from '../../../Constants';
import { useKaziNavigate } from '../../../Hooks/useKaziNavigate';
import { useGetClientsQuery } from '../../../Redux/api/clientApi';
import { useGetTalentsQuery } from '../../../Redux/api/talentApi';
import { KaziLayout } from '../../../Shared Components/Kazi/Layouts/KaziLayout';
import { Resume } from '../../../Types/Resume';
import { Affiliate, Talent } from '../../../Types/Talent';
import { getUserTypeFromLocator, toPascalCase } from '../../../Utils/String';
import { getHasRequiredFields, isTalent } from '../../../Utils/TalentUtils';

interface IProps {}

export const UserManagement = ({}: IProps) => {
  /* ******************** Hooks ******************** */

  const { navigateToTalent, navigateToClient } = useKaziNavigate();

  const {
    data: talents,
    isLoading: talentsLoading,
    isFetching: talentsFetching,
  } = useGetTalentsQuery();

  const {
    data: clients,
    isLoading: clientsLoading,
    isFetching: clientsFetching,
  } = useGetClientsQuery();

  const [searchParams] = useSearchParams();

  /* ******************** States ******************** */
  let defaultSearchParam = '';

  if (searchParams.toString().toString() !== '') {
    defaultSearchParam = labels.all; // to make the select options show all and look uniform with others
  }

  const [selectedCountry, setSelectedCountry] = useState(
    searchParams.get('country') || defaultSearchParam,
  );
  const [selectedAffiliate, setSelectedAffiliate] = useState(
    searchParams.get('affiliate') || defaultSearchParam,
  );
  const [selectedSpecialization, setSelectedSpecialization] = useState(
    searchParams.get('specialization') || defaultSearchParam,
  );
  const [profileCompleteness, setProfileCompleteness] = useState(
    searchParams.get('profile') || defaultSearchParam,
  );
  const [fetchByHireStatus, setFetchByHireStatus] = useState(
    searchParams.get('hired') || defaultSearchParam,
  );
  const [selectedType, setSelectedType] = useState(
    searchParams.get('type') || defaultSearchParam,
  ); // "Talent" or "Client"

  /* ******************** Variables ******************** */
  const columns: GridColDef[] = [
    { field: 'first_name', headerName: 'First Name', flex: 1 },
    { field: 'last_name', headerName: 'Last Name', flex: 1 },
    { field: 'email', headerName: 'Email', flex: 1 },
    { field: 'phone', headerName: 'Phone Number', flex: 1 },
    { field: 'country', headerName: 'Country', flex: 1 },
    {
      field: 'profile_completeness',
      headerName: 'Profile Completeness',
      flex: 1,
    },
    {
      field: 'affiliate',
      headerName: 'Affiliate',
      flex: 1,
      valueGetter: (params: Affiliate | null) => params?.name ?? '-',
    },
    {
      field: 'locator',
      headerName: 'Type',
      flex: 1,
      valueGetter: (params: string) =>
        toPascalCase(getUserTypeFromLocator(params) ?? ''),
    },
    {
      field: 'resume',
      headerName: 'Specialization',
      flex: 1,
      valueGetter: (params: Resume | null) =>
        params?.expertise?.specialization ?? '-', // Extract specialization if it exists
    },
  ];

  const data = useMemo(() => {
    if (!clients || !talents) return [];

    const all = [...talents, ...clients];

    return all.filter(item => {
      const matchesCountry =
        selectedCountry === labels.all || selectedCountry === ''
          ? true
          : item.country === selectedCountry;

      let matchesProfile = false;

      const isClient = getUserTypeFromLocator(item.locator) === 'client';

      if (
        profileCompleteness === labels.all ||
        profileCompleteness === '' ||
        isClient
      ) {
        matchesProfile = true;
      } else if (profileCompleteness === labels.completed) {
        matchesProfile = getHasRequiredFields(item as Talent);
      } else {
        matchesProfile = !getHasRequiredFields(item as Talent);
      }

      const matchesAffiliate =
        selectedAffiliate === labels.all || selectedAffiliate === ''
          ? true
          : item.affiliate?.name === selectedAffiliate;

      const typeFromLocator = getUserTypeFromLocator(item.locator); // returns "client" or "talent"
      const matchesType =
        selectedType === labels.all || selectedType === ''
          ? true
          : toPascalCase(typeFromLocator!) === selectedType;

      let isHired = false;

      if ('is_hired' in item) {
        isHired = item.is_hired;
      }

      let matchesHired = false;
      if (fetchByHireStatus === labels.all || fetchByHireStatus === '') {
        matchesHired = true;
      } else if (fetchByHireStatus === labels.hired) {
        matchesHired = isHired;
      }

      const matchesSpecialization =
        selectedSpecialization === labels.all || selectedSpecialization === ''
          ? true
          : isTalent(item) &&
            item?.resume?.expertise?.specialization === selectedSpecialization;

      return (
        matchesCountry &&
        matchesProfile &&
        matchesType &&
        matchesAffiliate &&
        matchesHired &&
        matchesSpecialization
      );
    });
  }, [
    clients,
    talents,
    selectedCountry,
    profileCompleteness,
    selectedAffiliate,
    selectedType,
    fetchByHireStatus,
    selectedSpecialization,
  ]);

  const isLoading =
    talentsLoading || talentsFetching || clientsLoading || clientsFetching;

  /* ******************** Functions ******************** */
  const handleNavigate = (locator: string) => {
    const isClient = getUserTypeFromLocator(locator) === 'client';
    if (isClient) {
      navigateToClient(locator);
    } else {
      navigateToTalent(locator);
    }
  };

  const exportToExcel = () => {
    // Prepare the rows in a clean format
    const worksheetData = data.map(row => {
      const resultRow: Record<string, any> = {};

      columns.forEach(col => {
        // Handle fields with valueGetter or nested data (like affiliate and specialization and type)
        if (col.field === 'affiliate') {
          resultRow[col.headerName || col.field] = row.affiliate?.name ?? '';
        } else if (col.field === 'resume') {
          resultRow[col.headerName || col.field] =
            // @ts-ignore
            row?.resume?.expertise?.specialization ?? '';
        } else if (col.field === 'locator') {
          resultRow[col.headerName || col.field] = toPascalCase(
            getUserTypeFromLocator(row.locator) ?? '',
          );
        } else if (col.valueGetter) {
          try {
            // Resolve custom `valueGetter`
            resultRow[col.headerName || col.field] =
              // @ts-ignore
              col.valueGetter({
                row: row,
                field: col.field,
                value: row[col.field as keyof typeof row],
              });
          } catch (error) {
            console.error(
              `Error processing valueGetter for ${col.field}:`,
              error,
            );
            resultRow[col.headerName || col.field] = 'Error';
          }
        } else {
          // Default case: Use the raw field value
          resultRow[col.headerName || col.field] =
            row[col.field as keyof typeof row] ?? '';
        }
      });

      return resultRow;
    });

    // Create a new worksheet
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);

    // Create a new workbook and append our worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Users');

    // Trigger Excel export
    XLSX.writeFile(workbook, 'Users.xlsx');
  };

  /* ******************** JSX ******************** */
  return (
    <KaziLayout>
      <Box display="flex" gap={2} mb={2} justifyContent="flex-end">
        <Button variant="contained" color="primary" onClick={exportToExcel}>
          Export
        </Button>
      </Box>

      <Box
        display="flex"
        gap={2}
        mb={2}
        justifyContent="space-between"
        flexWrap="wrap"
        flexGrow={1}
        alignItems="stretch"
        sx={{
          '& > *': {
            flexGrow: 1,
            flexBasis: { xs: '100%', sm: 'calc(50% - 16px)', md: 'auto' },
            maxWidth: 'calc(50% - 16px)', // Ensure that items stay within bounds on larger screens
          },
        }}
      >
        <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{labels.country}</InputLabel>
          <Select
            value={selectedCountry}
            onChange={(e: SelectChangeEvent) =>
              setSelectedCountry(e.target.value)
            }
            label={labels.country}
          >
            <MenuItem value={labels.all}>{labels.all}</MenuItem>
            {[...new Set(data?.map(item => item.country).filter(Boolean))].map(
              country => (
                <MenuItem key={country} value={country}>
                  {country}
                </MenuItem>
              ),
            )}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{labels.userType}</InputLabel>
          <Select
            value={selectedType}
            onChange={(e: SelectChangeEvent) => setSelectedType(e.target.value)}
            label={labels.userType}
          >
            <MenuItem value={labels.all}>{labels.all}</MenuItem>
            <MenuItem value={labels.talent}>{labels.talent}</MenuItem>
            <MenuItem value={labels.client}>{labels.client}</MenuItem>
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{labels.profile}</InputLabel>
          <Select
            value={profileCompleteness}
            onChange={(e: SelectChangeEvent) =>
              setProfileCompleteness(e.target.value)
            }
            label={labels.profile}
          >
            <MenuItem value={labels.all}>{labels.all}</MenuItem>
            <MenuItem value={labels.completed}>{labels.completed}</MenuItem>
            <MenuItem value={labels.incomplete}>{labels.incomplete}</MenuItem>
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{labels.affiliate}</InputLabel>
          <Select
            value={selectedAffiliate}
            onChange={(e: SelectChangeEvent) =>
              setSelectedAffiliate(e.target.value)
            }
            label={labels.affiliate}
          >
            <MenuItem value={labels.all}>{labels.all}</MenuItem>
            {Array.from(
              new Set(
                (talents ?? [])
                  .map(item => item.affiliate?.name)
                  .filter((name): name is string => Boolean(name)),
              ),
            ).map(affiliateName => (
              <MenuItem key={affiliateName} value={affiliateName}>
                {affiliateName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{labels.hiringStatus}</InputLabel>
          <Select
            value={fetchByHireStatus}
            onChange={(e: SelectChangeEvent) =>
              setFetchByHireStatus(e.target.value)
            }
            label={labels.hiringStatus}
          >
            <MenuItem value={labels.all}>{labels.all}</MenuItem>
            <MenuItem value={labels.hired}>{labels.hired}</MenuItem>
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{labels.specialization}</InputLabel>
          <Select
            value={selectedSpecialization}
            onChange={(e: SelectChangeEvent) =>
              setSelectedSpecialization(e.target.value)
            }
            label={labels.specialization}
          >
            <MenuItem value={labels.all}>{labels.all}</MenuItem>
            {Array.from(
              new Set(
                (talents ?? [])
                  .map(item => item?.resume?.expertise?.specialization)
                  .filter((name): name is string => Boolean(name)),
              ),
            ).map(specializationName => (
              <MenuItem key={specializationName} value={specializationName}>
                {specializationName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <DataGrid
        getRowId={row => row.locator}
        rows={data}
        onRowClick={row => handleNavigate(String(row.id))}
        loading={isLoading}
        {...{ columns }}
      />
    </KaziLayout>
  );
};
